import type { ReactNode } from 'react';
import { LeafIcon, PackageIcon, PathIcon, ScalesIcon } from '@phosphor-icons/react';

import { StatCard, type StatCardVariant } from 'components/stat-card/StatCard';
import { useTranslation } from 'contexts/translation-context';
import { decimateAndFormatNumber } from '@utils/number';
import classNames from '@utils/classnames';

type SustainabilityMetric = 'orders' | 'distance' | 'weight' | 'emissions';

export interface SustainabilityTotals {
  totalOrders: number;
  totalDistance: number;
  totalWeight: number;
  totalEmissions: number;
}

const METRIC_ICONS: Record<SustainabilityMetric, ReactNode> = {
  orders: <PackageIcon weight="bold" />,
  distance: <PathIcon weight="bold" />,
  weight: <ScalesIcon weight="bold" />,
  emissions: <LeafIcon weight="bold" />,
};

function SustainabilityMetricCard({
  metric,
  title,
  value,
  unit,
  variant = 'default',
}: {
  metric: SustainabilityMetric;
  title: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  variant?: StatCardVariant;
}) {
  return <StatCard label={title} icon={METRIC_ICONS[metric]} value={value} unit={unit} variant={variant} />;
}

const GRID_CLASS = 'grid gap-5 grid-cols-2 lg:grid-cols-4';

export function SustainabilityTotalsGrid({
  results,
  variant = 'default',
  className = '',
}: {
  results: SustainabilityTotals;
  variant?: StatCardVariant;
  className?: string;
}) {
  const { i18n } = useTranslation();

  return (
    <div className={classNames(GRID_CLASS, className)}>
      <SustainabilityMetricCard
        metric="orders"
        title={i18n('customerPortal.sustainability.orderCount')}
        value={results.totalOrders}
        variant={variant}
      />
      <SustainabilityMetricCard
        metric="distance"
        title={i18n('customerPortal.sustainability.totalDistance')}
        value={decimateAndFormatNumber(results.totalDistance / 10, 2, { decimalSeperator: ',' })}
        unit="km"
        variant={variant}
      />
      <SustainabilityMetricCard
        metric="weight"
        title={i18n('customerPortal.sustainability.totalWeight')}
        value={decimateAndFormatNumber(results.totalWeight / 1000, 2, { decimalSeperator: ',' })}
        unit="ton"
        variant={variant}
      />
      <SustainabilityMetricCard
        metric="emissions"
        title={i18n('customerPortal.sustainability.totalEmissions')}
        value={decimateAndFormatNumber(results.totalEmissions / 10, 2, { decimalSeperator: ',' })}
        unit="kg co2eq"
        variant={variant}
      />
    </div>
  );
}

export function SustainabilityAveragesGrid({
  results,
  variant = 'default',
}: {
  results: SustainabilityTotals;
  variant?: StatCardVariant;
}) {
  const { i18n } = useTranslation();
  const divider = results.totalOrders || 1;

  return (
    <div className={GRID_CLASS}>
      <SustainabilityMetricCard
        metric="distance"
        title={i18n('customerPortal.sustainability.totalDistance')}
        value={decimateAndFormatNumber(results.totalDistance / divider / 10, 2, { decimalSeperator: ',' })}
        unit="km"
        variant={variant}
      />
      <SustainabilityMetricCard
        metric="weight"
        title={i18n('customerPortal.sustainability.totalWeight')}
        value={decimateAndFormatNumber(results.totalWeight / divider, 2, { decimalSeperator: ',' })}
        unit="kg"
        variant={variant}
      />
      <SustainabilityMetricCard
        metric="emissions"
        title={i18n('customerPortal.sustainability.totalEmissions')}
        value={decimateAndFormatNumber(results.totalEmissions / divider / 10, 2, { decimalSeperator: ',' })}
        unit="kg co2eq"
        variant={variant}
      />
    </div>
  );
}
